/** Open state for the Preferences modal, held outside React so a boot-time address, a keyboard shortcut or any feature can open it before the modal has mounted. The open tab is mirrored into the address as #preferences/[tab] so a reload or a shared link lands on the same tab. */
import { useSyncExternalStore } from 'react';
import { requestSettingsNavigation } from '../../platform/navigation/settingsNavigationGuard';
import { NativeBridge } from '../../services/NativeBridge';
import { isPreferenceSectionId } from './preferenceSectionIds';
import { DEFAULT_SETTINGS_TAB, getSettingsTab, isSettingsTabId, settingsTabFor, type SettingsTabId } from './settingsTabs';

export interface SettingsModalState {
  open: boolean;
  tab: SettingsTabId;
  /** Set on phones once a tab is chosen; with it unset the phone sheet shows the tab list. */
  drilledIn: boolean;
}

const HASH_PREFIX = '#preferences';

let modalIntent = 0;
let state: SettingsModalState = { open: false, tab: DEFAULT_SETTINGS_TAB, drilledIn: false };
const listeners = new Set<() => void>();

function commit(next: SettingsModalState) {
  state = next;
  writeHash();
  listeners.forEach(listener => listener());
}

/** A hash the page held before the modal took it over, put back on close. */
let displacedHash = '';

/** replaceState rather than push: Back should leave the page the user was on, not step through tabs. */
function writeHash() {
  if (typeof window === 'undefined') return;
  const { pathname, search, hash } = window.location;
  const owned = hash === HASH_PREFIX || hash.startsWith(`${HASH_PREFIX}/`);
  if (state.open && !owned) displacedHash = hash;
  const next = state.open ? (state.drilledIn ? `${HASH_PREFIX}/${state.tab}` : HASH_PREFIX) : owned ? displacedHash : hash;
  if (!state.open) displacedHash = '';
  if (next === hash) return;
  window.history.replaceState(window.history.state, '', `${pathname}${search}${next}`);
}

/** What an address hash asks of the modal: undefined when the hash is not ours, null for the modal with no tab named, else the tab. */
export function parseSettingsHash(hash: string): SettingsTabId | null | undefined {
  if (hash !== HASH_PREFIX && !hash.startsWith(`${HASH_PREFIX}/`)) return undefined;
  const raw = hash.slice(HASH_PREFIX.length + 1);
  try {
    return settingsTabFor(decodeURIComponent(raw));
  } catch {
    // A hand-edited link can carry a broken escape; it still opens Preferences, just on no particular tab.
    return null;
  }
}

/** The native shells keep Preferences as a full page under their own sidebar and sheets; only the browser app uses the modal. */
export function usesSettingsModal(): boolean {
  return !NativeBridge.isRunningNatively();
}

/** Opens the modal on the tab holding `target` (a tab or an older section id). With no target a phone starts at the tab list. */
export function openSettingsModal(target?: string | null): void | Promise<void> {
  const intent = ++modalIntent;
  const tab = settingsTabFor(target);
  const next = { open: true, tab: tab ?? state.tab, drilledIn: tab !== null };
  // Availability can replace a live page editor. Let its pending save or
  // discard guard settle before the modal takes ownership of that schedule.
  if (next.tab === 'availability') return requestSettingsNavigation().then(approved => {
    if (intent !== modalIntent) return;
    if (approved) commit(next);
    else writeHash();
  });
  commit(next);
}

/** Keeps drilledIn as it was, so a phone sheet sliding away does not flip back to the list mid-exit; opening resets it. */
export function closeSettingsModal() {
  modalIntent += 1;
  if (state.open) commit({ ...state, open: false });
  else writeHash();
}

export function selectSettingsTab(tab: SettingsTabId) {
  modalIntent += 1;
  commit({ ...state, tab, drilledIn: true });
}

export function showSettingsTabList() {
  modalIntent += 1;
  commit({ ...state, drilledIn: false });
}

/** Opens personal Preferences from anywhere: the modal in the browser, the full page in the native shells. */
export function openSettings(target?: string | null) {
  if (usesSettingsModal()) {
    return openSettingsModal(target);
  }
  // The page is addressed by section, so a tab id opens its first section.
  const section = isPreferenceSectionId(target) ? target : isSettingsTabId(target) ? getSettingsTab(target).sections[0] : undefined;
  window.dispatchEvent(new CustomEvent('app:navigate', { detail: { view: 'preferences', settingsSection: section } }));
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useSettingsModal(): SettingsModalState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Test seam: forget any open state between cases. */
export function resetSettingsModalForTests() {
  modalIntent += 1;
  state = { open: false, tab: DEFAULT_SETTINGS_TAB, drilledIn: false };
  displacedHash = '';
  listeners.forEach(listener => listener());
}
